import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'motion/react';
import { BodyText, withStyles } from 'vopay';
import { Title, UtilButton } from '#shared/components/UI';
import { PermissionItem, PermissionSection } from '../types';
import CollapsibleList from '../CollapsibleList/CollapsibleList';
import PermissionGroup from '../PermissionGroup/PermissionGroup';
import Permission from '../Permission/Permission';
import { usePermissionsContext } from '../PermissionsContext';
import styles from './PermissionContainer.module.scss';

const cn = withStyles(styles);

type PermissionContainerProps = {
  section: PermissionSection;
  depth: number;
};

/** A toggle's card holds the toggle and the fields drawn under it. Animated so a toggle that a dependency
 * reveals (or its whole card) grows in instead of popping. */
function ToggleGroup({ item }: { item: PermissionItem }) {
  return (
    <PermissionGroup animateShowHide>
      {/* AnimatePresence sits with the rows it manages, so a dependency-revealed child row animates in/out. */}
      <AnimatePresence initial={false}>
        <Permission key={item.code} item={item} />

        {item.children.map((child) => (
          <Permission key={child.code} item={child} />
        ))}
      </AnimatePresence>
    </PermissionGroup>
  );
}

/** One section: collapsible, with its on/total count and Enable All / Disable All, or always open. */
export default function PermissionContainer({ section, depth }: PermissionContainerProps) {
  const { editor, readOnly } = usePermissionsContext();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Permissions' });

  if (section.alwaysOpen) {
    return (
      <div style={{ '--VP__Permissions__triggerDepth': depth }} className={cn('sectionWrapper')}>
        <PermissionGroup>
          <PermissionGroup.Header key="header">{section.label}</PermissionGroup.Header>

          {/* AnimatePresence sits with the rows it manages, so a dependency-hidden row animates out. */}
          <AnimatePresence initial={false}>
            {section.items
              .flatMap((item) => [item, ...item.children])
              .map((item) => (
                <Permission key={item.code} item={item} />
              ))}
          </AnimatePresence>
        </PermissionGroup>
      </div>
    );
  }

  const stats = editor.getSectionStats(section);
  const hasToggles = stats.total > 0;
  const isAllEnabled = hasToggles && stats.enabled === stats.total;
  const toggles = section.items.filter((item) => item.type === 'boolean');
  const standaloneFields = section.items.filter((item) => item.type !== 'boolean');

  return (
    <CollapsibleList.Item open={editor.isFiltering} style={{ '--VP__Permissions__triggerDepth': depth }}>
      <CollapsibleList.Trigger className={cn(depth > 0 && 'nestedTrigger')}>
        <div className={cn('groupHeader')}>
          <div className={cn('groupInfo')}>
            {depth === 0 ? (
              <Title level="5">{section.label}</Title>
            ) : (
              <BodyText className={cn('subCollapsibleTitle')}>{section.label}</BodyText>
            )}

            {hasToggles && (
              <BodyText weight="rg" className={cn('groupCount')}>
                ({stats.enabled}/{stats.total})
              </BodyText>
            )}
          </div>

          {hasToggles && !editor.isFiltering && !readOnly && (
            // Keeps the button click from also toggling the section.
            <div className={cn('groupActions')} onClick={(e) => e.stopPropagation()}>
              {isAllEnabled ? (
                <UtilButton size="medium" onClick={() => editor.disableSection(section)}>
                  {t('disableAll', { defaultValue: 'Disable All' })}
                </UtilButton>
              ) : (
                <UtilButton size="medium" onClick={() => editor.enableSection(section)}>
                  {t('enableAll', { defaultValue: 'Enable All' })}
                </UtilButton>
              )}
            </div>
          )}
        </div>
      </CollapsibleList.Trigger>

      <CollapsibleList.Content>
        {section.items.length > 0 && (
          <div className={cn('settings')}>
            {/* The AnimatePresence stays mounted while the section is open, so a card a dependency adds or
                removes animates in/out — instead of the card mounting fresh with its rows already inside it,
                where the enter/exit would be suppressed. Every card here sets `animateShowHide` for that. */}
            <AnimatePresence initial={false}>
              {toggles.map((item) => (
                <ToggleGroup key={item.code} item={item} />
              ))}

              {standaloneFields.length > 0 && (
                <PermissionGroup key="standaloneFields" animateShowHide>
                  {/* AnimatePresence sits with the rows it manages, so a revealed/hidden field animates. */}
                  <AnimatePresence initial={false}>
                    {standaloneFields.map((item) => (
                      <Permission key={item.code} item={item} />
                    ))}
                  </AnimatePresence>
                </PermissionGroup>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Always render the list (empty = 0 height, invisible) so its AnimatePresence stays mounted while
            the parent section is open. A dependency toggle that reveals/hides a whole subsection then adds or
            removes a child of an already-mounted list, so it animates in/out — instead of the list mounting
            fresh with the subsection already inside it, where the enter/exit would be suppressed. */}
        <CollapsibleList className={cn(depth === 0 ? 'rootCollapsibleList' : 'subCollapsibleList')}>
          {section.sections.map((subSection) => (
            <PermissionContainer key={subSection.id} section={subSection} depth={depth + 1} />
          ))}
        </CollapsibleList>
      </CollapsibleList.Content>
    </CollapsibleList.Item>
  );
}
